// src/pages/EditClient.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiURL } from "../config/config";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import "react-toastify/dist/ReactToastify.css";
import BackButton from "../components/BackButton";
import ClientApproversFields from "../components/ClientApproversFields";
import ClientProjectsFields from "../components/ClientProjectsFields";

const emptyApprover = { name: "", email: "", position: "" };

const normalizeProjects = (projects = []) => {
  const seen = new Set();

  return (Array.isArray(projects) ? projects : []).reduce(
    (normalizedProjects, project) => {
      const name = String(project || "").trim();
      const normalizedName = name.toLocaleLowerCase();
      if (!name || seen.has(normalizedName)) return normalizedProjects;
      seen.add(normalizedName);
      normalizedProjects.push(name);
      return normalizedProjects;
    },
    []
  );
};

const updateTimesheetClientsCache = (clientId, clientPayload) => {
  try {
    const cacheKey = "timesheetClients";
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (!Array.isArray(cached?.clients)) return;

    const clientIndex = cached.clients.findIndex(
      (client) => String(client?._id || client?.id || "") === String(clientId)
    );
    if (clientIndex < 0) {
      localStorage.removeItem(cacheKey);
      return;
    }

    const clients = [...cached.clients];
    clients[clientIndex] = { ...clients[clientIndex], ...clientPayload };
    localStorage.setItem(
      cacheKey,
      JSON.stringify({ ...cached, clients, cachedAt: Date.now() })
    );
  } catch (error) {
    localStorage.removeItem("timesheetClients");
  }
};

const buildClientPayload = (data) => {
  const clientData = { ...data };
  delete clientData._id;
  delete clientData.__v;

  return {
    ...clientData,
    projects: normalizeProjects(data.projects),
    authorizedApprovers: (data.authorizedApprovers || [])
      .map((approver) => ({
        name: String(approver.name || "").trim(),
        email: String(approver.email || "").trim(),
        position: String(approver.position || "").trim(),
      }))
      .filter((approver) => approver.name || approver.email || approver.position),
  };
};

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState({
    customerName: "",
    companyBaseName: "",
    email: "",
    address: "",
    taxIdentificationNumber: "",
    contactPhoneNumber: "",
    branchNo: "00000",
    projects: [],
    authorizedApprovers: [],
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}clients/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const client = response.data?.client || response.data;
        setClientData((prev) => ({
          ...prev,
          ...client,
          email: client?.email || "",
          branchNo: client?.branchNo || "00000",
          projects: Array.isArray(client?.projects) ? client.projects : [],
          authorizedApprovers: Array.isArray(client?.authorizedApprovers)
            ? client.authorizedApprovers.map((approver) => ({
                name: approver.name || "",
                email: approver.email || "",
                position: approver.position || "",
              }))
            : [],
        }));
      } catch (error) {
        toast.error("ไม่สามารถโหลดข้อมูลลูกค้าได้ ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));
  };

  const handleApproverChange = (index, field, value) => {
    setClientData((prev) => ({
      ...prev,
      authorizedApprovers: prev.authorizedApprovers.map((approver, approverIndex) =>
        approverIndex === index ? { ...approver, [field]: value } : approver
      ),
    }));
  };

  const handleProjectChange = (index, value) => {
    setClientData((prev) => ({
      ...prev,
      projects: prev.projects.map((project, projectIndex) =>
        projectIndex === index ? value : project
      ),
    }));
  };

  const handleAddProject = () => {
    setClientData((prev) => ({
      ...prev,
      projects: [...prev.projects, ""],
    }));
  };

  const handleRemoveProject = (index) => {
    setClientData((prev) => ({
      ...prev,
      projects: prev.projects.filter((_, projectIndex) => projectIndex !== index),
    }));
  };

  const handleAddApprover = () => {
    setClientData((prev) => ({
      ...prev,
      authorizedApprovers: [...prev.authorizedApprovers, { ...emptyApprover }],
    }));
  };

  const handleRemoveApprover = (index) => {
    setClientData((prev) => ({
      ...prev,
      authorizedApprovers: prev.authorizedApprovers.filter(
        (_, approverIndex) => approverIndex !== index
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      const payload = buildClientPayload(clientData);
      await axios.put(`${apiURL}clients/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      updateTimesheetClientsCache(id, payload);

      toast.success("อัปเดตข้อมูลลูกค้าสำเร็จ ✅");
      navigate("/clients");
    } catch (error) {
      console.error(error);
      toast.error("เกิดข้อผิดพลาดในการอัปเดต ❌");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <ClipLoader size={60} color="#6366F1" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 py-10 flex justify-center items-start transition-colors duration-300">
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-md rounded-xl w-full max-w-3xl p-6 sm:p-8 mt-6 sm:mt-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            แก้ไขข้อมูลลูกค้า
          </h2>
          <BackButton label="ย้อนกลับ" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 text-sm">
          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              ชื่อลูกค้า (Customer Name)
            </label>
            <input
              type="text"
              name="customerName"
              value={clientData.customerName}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              required
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              ชื่อบริษัทหลัก (Company Base Name)
            </label>
            <input
              type="text"
              name="companyBaseName"
              value={clientData.companyBaseName}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={clientData.email || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              placeholder="client@example.com"
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              ที่อยู่ (Address)
            </label>
            <textarea
              name="address"
              value={clientData.address}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              rows={3}
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              เลขประจำตัวผู้เสียภาษี (Tax ID)
            </label>
            <input
              type="text"
              name="taxIdentificationNumber"
              value={clientData.taxIdentificationNumber}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              เบอร์ติดต่อ (Contact Phone)
            </label>
            <input
              type="text"
              name="contactPhoneNumber"
              value={clientData.contactPhoneNumber}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
          </div>

          <div>
            <label className="font-medium text-gray-700 dark:text-gray-300">
              Branch No.
            </label>
            <input
              type="text"
              name="branchNo"
              value={clientData.branchNo || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              placeholder="00000"
            />
          </div>

          <ClientProjectsFields
            projects={clientData.projects}
            onProjectChange={handleProjectChange}
            onAddProject={handleAddProject}
            onRemoveProject={handleRemoveProject}
          />

          <ClientApproversFields
            approvers={clientData.authorizedApprovers}
            onApproverChange={handleApproverChange}
            onAddApprover={handleAddApprover}
            onRemoveApprover={handleRemoveApprover}
          />

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-1/2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium px-6 py-2.5 rounded-md shadow-sm transition-all duration-200"
            >
              ย้อนกลับ
            </button>

            <button
              type="submit"
              className="w-full sm:w-1/2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-6 py-2.5 rounded-md shadow-sm transition-all duration-200"
            >
              บันทึกการแก้ไข
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClient;
